import { Color, Vec3 } from 'playcanvas';

import { Events } from './events';
import type { ReconstructionTimeline, ReconstructionTimelineParticipantSample, ReconstructionTimelineScrubState } from './reconstruction-timeline';
import type { Scene } from './scene';

type PlaybackStatus = 'stopped' | 'playing' | 'paused';

type PlaybackSnapshot = {
    status: PlaybackStatus;
};

type PathRenderingDebugState = {
    interpolation: 'linear';
    activeTime: number;
    participantCount: number;
    sampleCount: number;
    playbackStatus: PlaybackStatus;
    metrics: {
        participantsConsidered: number;
        segmentsConsidered: number;
        segmentsDrawn: number;
        partialSegmentsDrawn: number;
        markerCount: number;
        drawCalls: number;
        updateMs: number;
        drawMs: number;
        totalUpdateCalls: number;
        totalRenderCalls: number;
        totalScrubAdvances: number;
        clampAtDurationCount: number;
    };
};

const clamp01 = (value: number) => {
    return Math.max(0, Math.min(1, value));
};

class ReconstructionTimelinePathRenderer {
    private readonly events: Events;

    private timeline?: ReconstructionTimeline;

    private scrubState?: ReconstructionTimelineScrubState;

    private playbackStatus: PlaybackStatus = 'stopped';

    private followEnabled = false;

    private readonly lineStart = new Vec3();

    private readonly lineEnd = new Vec3();

    private readonly markerCenter = new Vec3();

    private readonly markerOffset = new Vec3();

    private readonly pathColor = new Color(1, 1, 1, 0.75);

    private readonly markerColor = new Color(1, 1, 1, 1);

    private readonly headingColor = new Color(1, 1, 1, 1);

    private readonly partialEnd = new Vec3();

    private readonly followPosition = new Vec3();

    private readonly followTarget = new Vec3();

    private frameParticipantsConsidered = 0;

    private frameSegmentsConsidered = 0;

    private frameSegmentsDrawn = 0;

    private framePartialSegmentsDrawn = 0;

    private frameMarkerCount = 0;

    private frameDrawCalls = 0;

    private lastUpdateMs = 0;

    private lastDrawMs = 0;

    private totalUpdateCalls = 0;

    private totalRenderCalls = 0;

    private totalScrubAdvances = 0;

    private clampAtDurationCount = 0;

    private lastDebug: PathRenderingDebugState = {
        interpolation: 'linear',
        activeTime: 0,
        participantCount: 0,
        sampleCount: 0,
        playbackStatus: 'stopped',
        metrics: {
            participantsConsidered: 0,
            segmentsConsidered: 0,
            segmentsDrawn: 0,
            partialSegmentsDrawn: 0,
            markerCount: 0,
            drawCalls: 0,
            updateMs: 0,
            drawMs: 0,
            totalUpdateCalls: 0,
            totalRenderCalls: 0,
            totalScrubAdvances: 0,
            clampAtDurationCount: 0
        }
    };

    constructor(events: Events) {
        this.events = events;

        this.timeline = this.events.invoke('reconstructionTimeline.get') as ReconstructionTimeline | undefined;
        this.scrubState = this.events.invoke('reconstructionTimeline.scrubState') as ReconstructionTimelineScrubState | undefined;

        this.events.on('reconstructionTimeline.changed', (timeline?: ReconstructionTimeline) => {
            this.timeline = timeline;
            this.ensureRender();
        });

        this.events.on('reconstructionTimeline.scrubChanged', (state?: ReconstructionTimelineScrubState) => {
            this.scrubState = state;
            this.ensureRender();
        });

        this.events.on('tour.playbackChanged', (snapshot: PlaybackSnapshot) => {
            if (snapshot?.status === 'playing' || snapshot?.status === 'paused' || snapshot?.status === 'stopped') {
                this.playbackStatus = snapshot.status;
                this.events.fire('cameraPlayback.refresh');
                this.ensureRender();
            }
        });

        this.events.on('camera.controller', () => {
            this.setFollowEnabled(false);
        });

        this.events.on('scene.clear', () => {
            this.timeline = undefined;
            this.scrubState = undefined;
            this.playbackStatus = 'stopped';
            this.events.fire('cameraPlayback.refresh');
            this.lastDebug = {
                interpolation: 'linear',
                activeTime: 0,
                participantCount: 0,
                sampleCount: 0,
                playbackStatus: 'stopped',
                metrics: {
                    participantsConsidered: 0,
                    segmentsConsidered: 0,
                    segmentsDrawn: 0,
                    partialSegmentsDrawn: 0,
                    markerCount: 0,
                    drawCalls: 0,
                    updateMs: 0,
                    drawMs: 0,
                    totalUpdateCalls: 0,
                    totalRenderCalls: 0,
                    totalScrubAdvances: 0,
                    clampAtDurationCount: 0
                }
            };
        });

        this.events.on('update', (deltaTime: number) => {
            this.update(deltaTime);
        });

        this.events.on('prerender', () => {
            this.render();
        });

        this.events.function('reconstructionTimeline.pathRenderingDebug', () => {
            return {
                ...this.lastDebug
            };
        });

        this.events.function('reconstructionTimeline.followEnabled', () => {
            return this.followEnabled;
        });

        this.events.function('reconstructionTimeline.followCameraActive', () => {
            return this.followEnabled && this.playbackStatus === 'playing';
        });

        this.events.function('reconstructionTimeline.setFollowEnabled', (value: unknown) => {
            return this.setFollowEnabled(value);
        });

        this.events.on('reconstructionTimeline.setFollowEnabled', (value: unknown) => {
            this.setFollowEnabled(value);
        });
    }

    private setFollowEnabled(value: unknown) {
        const next = value === true;
        if (next === this.followEnabled) {
            return true;
        }

        this.followEnabled = next;
        this.events.fire('reconstructionTimeline.followChanged', this.followEnabled);
        this.events.fire('cameraPlayback.refresh');
        this.ensureRender();
        return true;
    }

    private getScene() {
        return (window as Window & { scene?: Scene }).scene;
    }

    private ensureRender() {
        const scene = this.getScene();
        if (scene) {
            scene.forceRender = true;
        }
    }

    private getActiveTime() {
        if (!this.timeline) {
            return 0;
        }
        return this.scrubState ? this.scrubState.time : 0;
    }

    private update(deltaTime: number) {
        const updateStart = performance.now();
        this.totalUpdateCalls += 1;

        if (this.playbackStatus !== 'playing' || !this.timeline) {
            this.lastUpdateMs = performance.now() - updateStart;
            return;
        }

        if (typeof deltaTime !== 'number' || !Number.isFinite(deltaTime) || deltaTime <= 0) {
            this.lastUpdateMs = performance.now() - updateStart;
            return;
        }

        const duration = Math.max(0, this.timeline.duration);
        if (duration <= 0) {
            this.lastUpdateMs = performance.now() - updateStart;
            return;
        }

        const currentTime = this.getActiveTime();
        const nextTime = Math.min(duration, currentTime + deltaTime);
        if (nextTime >= duration && currentTime < duration && currentTime + deltaTime > duration) {
            this.clampAtDurationCount += 1;
        }

        if (Math.abs(nextTime - currentTime) <= 1e-6) {
            this.applyFollowCamera();
            this.lastUpdateMs = performance.now() - updateStart;
            return;
        }

        this.events.fire('reconstructionTimeline.setScrubTime', nextTime);
        this.totalScrubAdvances += 1;
        this.applyFollowCamera();
        this.lastUpdateMs = performance.now() - updateStart;
    }

    private applyFollowCamera() {
        if (!this.timeline || this.playbackStatus !== 'playing' || !this.followEnabled) {
            return;
        }

        const leadParticipant = this.timeline.participants[0];
        if (!leadParticipant) {
            return;
        }

        const time = this.getActiveTime();
        const samples = this.events.invoke('reconstructionTimeline.sampleParticipants', time) as ReconstructionTimelineParticipantSample[];
        const leadSample = samples.find(sample => sample.id === leadParticipant.id) ?? samples[0];
        if (!leadSample) {
            return;
        }

        const followDistance = 2.5;
        const followHeight = 1.2;
        const targetHeight = 0.8;
        const headingRad = leadSample.heading * Math.PI / 180;
        const forwardX = Math.sin(headingRad);
        const forwardZ = Math.cos(headingRad);

        this.followPosition.set(
            leadSample.position[0] - forwardX * followDistance,
            leadSample.position[1] + followHeight,
            leadSample.position[2] - forwardZ * followDistance
        );

        this.followTarget.set(
            leadSample.position[0],
            leadSample.position[1] + targetHeight,
            leadSample.position[2]
        );

        this.events.fire('camera.setPose', {
            position: this.followPosition.clone(),
            target: this.followTarget.clone()
        }, 0);
    }

    private drawSegment(scene: Scene, from: [number, number, number], to: [number, number, number], color: Color) {
        this.lineStart.set(from[0], from[1], from[2]);
        this.lineEnd.set(to[0], to[1], to[2]);
        scene.app.drawLine(this.lineStart, this.lineEnd, color, true, scene.debugLayer);
        this.frameDrawCalls += 1;
    }

    private resetFrameMetrics() {
        this.frameParticipantsConsidered = 0;
        this.frameSegmentsConsidered = 0;
        this.frameSegmentsDrawn = 0;
        this.framePartialSegmentsDrawn = 0;
        this.frameMarkerCount = 0;
        this.frameDrawCalls = 0;
    }

    private buildDebugState(activeTime: number, participantCount: number, sampleCount: number): PathRenderingDebugState {
        return {
            interpolation: 'linear',
            activeTime,
            participantCount,
            sampleCount,
            playbackStatus: this.playbackStatus,
            metrics: {
                participantsConsidered: this.frameParticipantsConsidered,
                segmentsConsidered: this.frameSegmentsConsidered,
                segmentsDrawn: this.frameSegmentsDrawn,
                partialSegmentsDrawn: this.framePartialSegmentsDrawn,
                markerCount: this.frameMarkerCount,
                drawCalls: this.frameDrawCalls,
                updateMs: this.lastUpdateMs,
                drawMs: this.lastDrawMs,
                totalUpdateCalls: this.totalUpdateCalls,
                totalRenderCalls: this.totalRenderCalls,
                totalScrubAdvances: this.totalScrubAdvances,
                clampAtDurationCount: this.clampAtDurationCount
            }
        };
    }

    private drawPath(scene: Scene, participant: ReconstructionTimeline['participants'][number], activeTime: number) {
        this.frameParticipantsConsidered += 1;

        const keyframes = participant.pathKeyframes;
        if (keyframes.length < 2) {
            return;
        }

        const intensity = 0.35 + 0.65 * clamp01(participant.color[0] + participant.color[1] + participant.color[2]);
        this.pathColor.set(
            participant.color[0],
            participant.color[1],
            participant.color[2],
            Math.max(0.35, Math.min(0.85, intensity))
        );

        for (let i = 0; i < keyframes.length - 1; i++) {
            const from = keyframes[i];
            const to = keyframes[i + 1];
            if (activeTime <= from.time) {
                break;
            }

            this.frameSegmentsConsidered += 1;

            if (activeTime >= to.time) {
                this.drawSegment(scene, from.position, to.position, this.pathColor);
                this.frameSegmentsDrawn += 1;
                continue;
            }

            const interval = to.time - from.time;
            const alpha = interval > 0 ? clamp01((activeTime - from.time) / interval) : 0;
            this.partialEnd.set(
                from.position[0] + (to.position[0] - from.position[0]) * alpha,
                from.position[1] + (to.position[1] - from.position[1]) * alpha,
                from.position[2] + (to.position[2] - from.position[2]) * alpha
            );

            this.drawSegment(
                scene,
                from.position,
                [this.partialEnd.x, this.partialEnd.y, this.partialEnd.z],
                this.pathColor
            );
            this.frameSegmentsDrawn += 1;
            this.framePartialSegmentsDrawn += 1;
            break;
        }
    }

    private drawMarker(scene: Scene, sample: ReconstructionTimelineParticipantSample) {
        this.frameMarkerCount += 1;

        this.markerColor.set(sample.color[0], sample.color[1], sample.color[2], 1);
        this.headingColor.set(sample.color[0], sample.color[1], sample.color[2], 1);

        const markerSize = 0.12;
        this.markerCenter.set(sample.position[0], sample.position[1], sample.position[2]);

        this.markerOffset.set(markerSize, 0, 0);
        this.drawVecSegment(scene, this.markerCenter, this.markerOffset, this.markerColor);

        this.markerOffset.set(0, markerSize, 0);
        this.drawVecSegment(scene, this.markerCenter, this.markerOffset, this.markerColor);

        this.markerOffset.set(0, 0, markerSize);
        this.drawVecSegment(scene, this.markerCenter, this.markerOffset, this.markerColor);

        const headingLength = markerSize * 1.8;
        const headingRad = sample.heading * Math.PI / 180;
        this.lineStart.copy(this.markerCenter);
        this.lineEnd.set(
            sample.position[0] + Math.sin(headingRad) * headingLength,
            sample.position[1],
            sample.position[2] + Math.cos(headingRad) * headingLength
        );
        scene.app.drawLine(this.lineStart, this.lineEnd, this.headingColor, true, scene.debugLayer);
        this.frameDrawCalls += 1;
    }

    private drawVecSegment(scene: Scene, center: Vec3, axisOffset: Vec3, color: Color) {
        this.lineStart.set(center.x - axisOffset.x, center.y - axisOffset.y, center.z - axisOffset.z);
        this.lineEnd.set(center.x + axisOffset.x, center.y + axisOffset.y, center.z + axisOffset.z);
        scene.app.drawLine(this.lineStart, this.lineEnd, color, true, scene.debugLayer);
        this.frameDrawCalls += 1;
    }

    private render() {
        const drawStart = performance.now();
        this.totalRenderCalls += 1;
        this.resetFrameMetrics();

        const timeline = this.timeline;
        if (!timeline || timeline.participants.length === 0) {
            this.lastDrawMs = performance.now() - drawStart;
            this.lastDebug = this.buildDebugState(0, 0, 0);
            return;
        }

        const scene = this.getScene();
        if (!scene) {
            this.lastDrawMs = performance.now() - drawStart;
            return;
        }

        const activeTime = this.getActiveTime();

        for (const participant of timeline.participants) {
            this.drawPath(scene, participant, activeTime);
        }

        const samples = this.events.invoke('reconstructionTimeline.sampleParticipants', activeTime) as ReconstructionTimelineParticipantSample[];

        for (const sample of samples) {
            this.drawMarker(scene, sample);
        }

        this.lastDrawMs = performance.now() - drawStart;
        this.lastDebug = this.buildDebugState(activeTime, timeline.participants.length, samples.length);
    }
}

const registerReconstructionTimelinePathEvents = (events: Events) => {
    const renderer = new ReconstructionTimelinePathRenderer(events);
    return renderer;
};

export { registerReconstructionTimelinePathEvents };
export type { PathRenderingDebugState };
